/**
 * Shared recovery for stale-deployment chunk-load failures.
 *
 * A new deploy replaces every content-hashed asset filename. A browser tab
 * left open from before the deploy still holds the OLD index.html/main
 * bundle in memory, which references chunk filenames that no longer exist
 * on the server once the new build overwrites them — any not-yet-visited
 * React.lazy() route 404s on its dynamic import() the moment it's navigated
 * to. React.lazy() also caches the rejected import promise permanently, so
 * re-rendering throws the exact same cached rejection forever — only a full
 * page reload (which re-fetches the current index.html with correct asset
 * references) can recover.
 *
 * Two independent signals feed into this same recovery path:
 * - Vite's own `vite:preloadError` window event (structural, browser-
 *   independent — fires for any failed dynamic import used for code
 *   splitting), wired up in `index.tsx`.
 * - `ErrorBoundary`'s message-pattern match, as a fallback for failures
 *   that don't go through Vite's preload machinery (or a Vite version
 *   without the event) but still look like a chunk-load error by message.
 */
const CHUNK_RELOAD_FLAG = 'errorBoundary:chunkReloadAttempted';

/**
 * Reloads the page once per browser session. Returns true if it triggered
 * a reload, false if one was already attempted this session (so the caller
 * can fall through to a normal error UI instead of looping forever).
 */
export function attemptChunkReloadOnce(): boolean {
  let alreadyAttempted = false;
  try {
    alreadyAttempted = sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1';
  } catch {
    /* sessionStorage unavailable (private mode, etc.) — treat as not attempted */
  }

  if (alreadyAttempted) return false;

  try {
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
  } catch {
    /* ignore — worst case we reload more than once */
  }
  window.location.reload();
  return true;
}

export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}
